import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import CommunityLayout from '../../components/community/CommunityLayout';
import NoticeCard from '../../components/common/NoticeCard';
import LoginModal from '../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../services/postApi';
import { searchCommunityPosts } from '../../services/searchApi';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import type { PostSearchItem } from '../../services/searchApi';

const CommunityHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  
  // Redux 상태에서 로그인 정보 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 검색 관련 상태 - 초기값을 빈 배열로 명시적으로 설정
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 무한스크롤을 위한 상태
  const [posts, setPosts] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response: ApiResponse<PostListResponse> = await getPostList('home', pageNum, 10);
      
      if (response.data) {
        const newPosts = response.data.content;
        setPosts(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      /// console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [loading, navigate]);

  // 더 많은 데이터 로드
  const loadMorePosts = useCallback(() => {
    if (hasMorePosts && !loading && !isSearchMode) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  }, [hasMorePosts, loading, isSearchMode, page, fetchPosts]);

  // 무한스크롤 적용
  useInfiniteScroll({
    hasMore: hasMorePosts && !isSearchMode,
    isLoading: loading,
    onLoadMore: loadMorePosts,
    threshold: 200
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (!isSearchMode) {
      fetchPosts(0, true);
      setPage(0);
    }
  }, [isSearchMode]); // fetchPosts 제거하여 무한 루프 방지
  
  // 고정 공지사항 필터링 (pinned=true인 게시글)
  const noticePosts = posts.filter(post => post.pinned);
  // 일반 게시글 필터링 (pinned=false인 게시글)
  const regularPosts = posts.filter(post => !post.pinned);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]); // 검색 모드가 아닐 때 명시적으로 빈 배열로 초기화
    }
  }, [searchParams]);

  // 검색 실행 함수 - 에러 처리 강화
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchCommunityPosts(keyword);
      // 결과가 배열인지 확인하고 설정
      if (Array.isArray(results)) {
        setSearchResults(results);
      } else {
        /// console.error('검색 결과가 배열이 아닙니다:', results);
        setSearchResults([]);
        setSearchError('검색 결과 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      /// console.error('커뮤니티 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);  // 오류 발생 시 빈 배열로 설정
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community');
      }
    }
  };

  const handlePostClick = (postId: number) => {
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    navigate(`/community/post/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      navigate(`/community/post/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };

  // HTML 태그 제거 함수 (코드, 이미지, 첨부파일은 완전히 제거)
  const stripHtmlExceptCodeAndMedia = (html: string): string => {
    // HTML이 없거나 빈 문자열인 경우 처리
    if (!html || typeof html !== 'string') {
      return '';
    }

    // 임시 div 엘리먼트 생성
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // 코드 블록 완전히 제거
    const preElements = tempDiv.querySelectorAll('pre');
    preElements.forEach((pre) => {
      pre.remove();
    });
    
    // 인라인 코드 완전히 제거
    const codeElements = tempDiv.querySelectorAll('code');
    codeElements.forEach((code) => {
      code.remove();
    });
    
    // 이미지 태그 완전히 제거
    const imgElements = tempDiv.querySelectorAll('img');
    imgElements.forEach((img) => {
      img.remove();
    });
    
    // 첨부파일 관련 모든 요소 완전히 제거
    const attachmentLists = tempDiv.querySelectorAll('ul.tbc-attachments, ul');
    attachmentLists.forEach((ul) => {
      // 첨부파일 목록인지 확인 (li 안에 a 태그가 있는 경우)
      const hasAttachmentLinks = ul.querySelectorAll('li a').length > 0;
      if (hasAttachmentLinks) {
        ul.remove();
      }
    });
    
    // 모든 종류의 첨부파일 링크 완전히 제거
    const attachmentLinks = tempDiv.querySelectorAll('a[download], a[href*="blob:"], a[href*=".pdf"], a[href*=".doc"], a[href*=".zip"], a[href*=".jpg"], a[href*=".png"], a[href*=".gif"]');
    attachmentLinks.forEach((link) => {
      link.remove();
    });
    
    // 첨부파일 관련 클래스가 있는 모든 요소 제거
    const attachmentElements = tempDiv.querySelectorAll('.tbc-attachment, .tbc-attachment__link, .tbc-attachment__name, .attachment');
    attachmentElements.forEach((element) => {
      element.remove();
    });
    
    // 파일 확장자가 포함된 링크나 텍스트 제거
    const fileExtensionPattern = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z|tar|gz|jpg|jpeg|png|gif|bmp|svg|mp4|avi|mov|mp3|wav|txt|csv)$/i;
    const allLinks = tempDiv.querySelectorAll('a');
    allLinks.forEach((link) => {
      const href = link.getAttribute('href') || '';
      const text = link.textContent || '';
      if (fileExtensionPattern.test(href) || fileExtensionPattern.test(text)) {
        link.remove();
      }
    });
    
    // 나머지 HTML 태그만 제거하고 텍스트 내용 추출
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // 연속된 공백과 줄바꿈 정리
    return textContent.replace(/\s+/g, ' ').trim();
  };

  // HTML 태그 제거 및 텍스트 요약
  const getPlainText = (html: string, maxLength: number = 150) => {
    const cleanText = stripHtmlExceptCodeAndMedia(html);
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  return (
    <CommunityLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>커뮤니티</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티 홈</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 공지사항 섹션 - API에서 받은 고정 게시글이 있을 때만 표시 */}
          {noticePosts.length > 0 && (
            <NoticeSection>
              {noticePosts.map((notice, index) => (
                <NoticeCard 
                  key={`notice-${notice.post_id}`}
                  title={notice.title}
                  onClick={() => handlePostClick(notice.post_id)}
                />
              ))}
            </NoticeSection>
          )}

          {/* 게시글 목록 */}
          <PostsList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>
                    <EmptyMessage>{searchError}</EmptyMessage>
                    <EmptySubMessage>다시 시도해보세요.</EmptySubMessage>
                  </EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>
                    <EmptyMessage>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyMessage>
                    <EmptySubMessage>다른 검색어로 다시 검색해보세요.</EmptySubMessage>
                  </EmptyState>
                ) : (
                  // searchResults가 배열인지 확인하고 map 실행
                  Array.isArray(searchResults) && searchResults.length > 0 ? (
                    searchResults.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <PostCard onClick={() => handlePostClick(post.id)}>
                        <PostContent>
                          <PostHeader>
                            <PostTitle className="post-title">{post.title}</PostTitle>
                            <PostDescription>
                              {getPlainText(post.htmlcontent)}
                            </PostDescription>
                          </PostHeader>
                          
                          <PostMeta>
                            <PostCategoryTags>
                              {/* 카테고리별 조건부 렌더링 */}
                              {post.category === '강의 질문' ? (
                                <>
                                  <PostCategory>{post.category}</PostCategory>
                                  {post.lectureTitle && (
                                    <>
                                      <Divider />
                                      <PostCategory>{post.lectureTitle}</PostCategory>
                                    </>
                                  )}
                                  {Number(post.chapter) !== 0 && (
                                    <>
                                      <Divider />
                                      <PostCategory>챕터 {post.chapter}</PostCategory>
                                    </>
                                  )}
                                </>
                              ) : post.category === '스터디' ? (
                                <>
                                  <PostCategory>{post.category}</PostCategory>
                                  {post.studyCategoryName && (
                                    <>
                                      <Divider />
                                      <PostCategory>{post.studyCategoryName}</PostCategory>
                                    </>
                                  )}
                                </>
                              ) : (post.category === '정보 공유' || post.category === '공지사항') ? (
                                <PostCategory>{post.category}</PostCategory>
                              ) : (
                                // 기본 카테고리 표시
                                <PostCategory>{post.category}</PostCategory>
                              )}
                              
                              {/* 고정 게시글 표시는 모든 카테고리에서 공통 */}
                              {post.pinned && (
                                <>
                                  <Divider />
                                  <PostCategory style={{backgroundColor: '#ff6b6b', color: 'white'}}>📌 고정</PostCategory>
                                </>
                              )}
                            </PostCategoryTags>
                          </PostMeta>
                        </PostContent>
                        
                        <PostFooter>
                          <PostAuthorInfo>
                            <AuthorAvatar>
                              <img src={post.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{post.writerName}</AuthorName>
                            <Divider />
                            <PostDate>{new Date(post.postDate).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}</PostDate>
                          </PostAuthorInfo>
                          
                          {post.category !== '공지사항' && (
                            <PostStats>
                              <StatItem>{post.category === '강의 질문' ? '답변' : '댓글'} {post.replyCnt}</StatItem>
                              <Divider />
                              <StatItem>조회수 {post.viewCnt}</StatItem>
                            </PostStats>
                          )}
                        </PostFooter>
                      </PostCard>
                      {index < searchResults.length - 1 && <PostDivider />}
                    </React.Fragment>
                  ))
                  ) : (
                    // searchResults가 빈 배열이거나 배열이 아닌 경우
                    <EmptyState>
                      <EmptyMessage>검색 결과를 표시할 수 없습니다.</EmptyMessage>
                      <EmptySubMessage>다시 검색해보세요.</EmptySubMessage>
                    </EmptyState>
                  )
                )}
              </>
            ) : (
              // 일반 모드 (기본 데이터)
              <>
                {loading && regularPosts.length === 0 ? (
                  <LoadingState>게시글을 불러오는 중...</LoadingState>
                ) : regularPosts.length === 0 ? (
                  <EmptyState>
                    <EmptyMessage>아직 작성된 게시글이 없습니다.</EmptyMessage>
                    <EmptySubMessage>첫 번째 글을 작성해보세요!</EmptySubMessage>
                  </EmptyState>
                ) : (
                  regularPosts.map((post, index) => (
                    <React.Fragment key={post.post_id}>
                      <PostCard onClick={() => handlePostClick(post.post_id)}>
                        <PostContent>
                          <PostHeader>
                            <PostTitle className="post-title">{post.title}</PostTitle>
                            <PostDescription>
                              {getPlainText(post.content)}
                            </PostDescription>
                          </PostHeader>
                          
                          <PostMeta>
                            <PostCategoryTags>
                              {/* 카테고리별 조건부 렌더링 */}
                              {post.post_category === '강의 질문' ? (
                                <>
                                  <PostCategory>{post.post_category}</PostCategory>
                                  {post.lecture_name && (
                                    <>
                                      <Divider />
                                      <PostCategory>{post.lecture_name}</PostCategory>
                                    </>
                                  )}
                                  {Number(post.chapter) !== 0 && (
                                    <>
                                      <Divider />
                                      <PostCategory>챕터 {post.chapter}</PostCategory>
                                    </>
                                  )}
                                </>
                              ) : post.post_category === '스터디' ? (
                                <>
                                  <PostCategory>{post.post_category}</PostCategory>
                                  {post.study_name && (
                                    <>
                                      <Divider />
                                      <PostCategory>{post.study_name}</PostCategory>
                                    </>
                                  )}
                                </>
                              ) : (post.post_category === '정보 공유' || post.post_category === '공지사항') ? (
                                <PostCategory>{post.post_category}</PostCategory>
                              ) : (
                                // 기본 카테고리 표시
                                <PostCategory>{post.post_category}</PostCategory>
                              )}
                              
                              {/* 고정 게시글 표시는 모든 카테고리에서 공통 */}
                              {post.pinned && (
                                <>
                                  <Divider />
                                  <PostCategory style={{backgroundColor: '#ff6b6b', color: 'white'}}>📌 고정</PostCategory>
                                </>
                              )}
                            </PostCategoryTags>
                          </PostMeta>
                        </PostContent>
                        
                        <PostFooter>
                          <PostAuthorInfo>
                            <AuthorAvatar>
                              <img src={post.writer_profile_image_url || "/images/default-avatar.png"} alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{post.writer_name}</AuthorName>
                            <Divider />
                            <PostDate>{new Date(post.created_at).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}</PostDate>
                          </PostAuthorInfo>
                
                          {/* <PostStats>
                            <StatItem>{post.post_category === '강의 질문' ? '답변' : '댓글'} {post.reply_cnt}</StatItem>
                            <Divider />
                            <StatItem>조회수 {post.view_cnt}</StatItem>
                          </PostStats> */}
                          {post.post_category !== '공지사항' && (
                            <PostStats>
                              <StatItem>{post.post_category === '강의 질문' ? '답변' : '댓글'} {post.reply_cnt}</StatItem>
                              <Divider />
                              <StatItem>조회수 {post.view_cnt}</StatItem>
                            </PostStats>
                          )}
                        </PostFooter>
                      </PostCard>
                      {index < regularPosts.length - 1 && <PostDivider />}
                    </React.Fragment>
                  ))
                )}
                
                {/* 무한스크롤 로딩 표시 */}
                {loading && regularPosts.length > 0 && (
                  <LoadingState>추가 게시글을 불러오는 중...</LoadingState>
                )}
              </>
            )}
          </PostsList>
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px; /* 콘텐츠 영역의 최대 너비 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const NoticeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -32px;
`;

const PostsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PostCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
  cursor: pointer;
`;

const PostContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PostHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PostTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
  transition: color 0.2s;
`;

const PostDescription = styled.p`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.20px;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const PostCategoryTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const PostCategory = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.20px;
`;

const PostFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PostAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  background: white;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.20px;
`;

const PostDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.20px;
`;

const PostStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const PostDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyMessage = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
`;

const EmptySubMessage = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

export default CommunityHome;